import type { SessionTodo } from "./shared";

export type TodoAnchor = {
  parent: string | null;
  after: string | null;
};

export type TodoRestoreRow = {
  row: SessionTodo;
  at: TodoAnchor;
};

type CommandMetadata = {
  seq?: number;
  stamp?: { t: number; d: string };
};

export type TodoCommand =
  | (CommandMetadata & {
      kind: "create";
      id: string;
      text: string;
      completed: boolean;
      at: TodoAnchor;
    })
  | (CommandMetadata & { kind: "setText"; id: string; text: string })
  | (CommandMetadata & { kind: "delete"; id: string; deleteIds?: readonly string[] })
  | (CommandMetadata & {
      kind: "undelete";
      group: { epoch: number; seq: number };
      rows?: readonly TodoRestoreRow[];
    })
  | (CommandMetadata & { kind: "move"; id: string; at: TodoAnchor })
  | (CommandMetadata & { kind: "indent" | "outdent"; id: string })
  | (CommandMetadata & {
      kind: "complete" | "uncomplete";
      id: string;
      cascadeIds?: readonly string[];
    });

export type RankedTodo = Omit<SessionTodo, "parentId"> & {
  parentId: string | null;
  rank: number;
};

type TreeParts = {
  roots: SessionTodo[];
  children: Map<string, SessionTodo[]>;
};

function cloneTodo(todo: SessionTodo, parentId: string | null): SessionTodo {
  return parentId === null
    ? { id: todo.id, text: todo.text, completed: todo.completed }
    : { id: todo.id, text: todo.text, completed: todo.completed, parentId };
}

function treeParts(rows: readonly SessionTodo[]): TreeParts {
  const normalized = toTreeOrder(rows);
  const roots = normalized.filter((row) => !row.parentId);
  const children = new Map<string, SessionTodo[]>();
  for (const root of roots) children.set(root.id, []);
  for (const row of normalized) {
    if (row.parentId) children.get(row.parentId)?.push(row);
  }
  return { roots, children };
}

function flatten({ roots, children }: TreeParts): SessionTodo[] {
  return roots.flatMap((root) => [root, ...(children.get(root.id) ?? [])]);
}

function insertionIndex(siblings: readonly SessionTodo[], after: string | null): number {
  if (after === null) return 0;
  const predecessor = siblings.findIndex((row) => row.id === after);
  return predecessor < 0 ? siblings.length : predecessor + 1;
}

function validParent(parts: TreeParts, parent: string | null): string | null {
  return parent !== null && parts.roots.some((root) => root.id === parent) ? parent : null;
}

function insertAtAnchor(
  rows: readonly SessionTodo[],
  row: SessionTodo,
  anchor: TodoAnchor,
): SessionTodo[] {
  const parts = treeParts(rows);
  const parentId = validParent(parts, anchor.parent);
  const placed = cloneTodo(row, parentId);
  if (parentId === null) {
    parts.roots.splice(insertionIndex(parts.roots, anchor.after), 0, placed);
    parts.children.set(placed.id, []);
  } else {
    const siblings = parts.children.get(parentId) ?? [];
    siblings.splice(insertionIndex(siblings, anchor.after), 0, placed);
    parts.children.set(parentId, siblings);
  }
  return flatten(parts);
}

/** Restore the one-level tree invariant and make every child contiguous with its root. */
export function toTreeOrder(rows: readonly SessionTodo[]): SessionTodo[] {
  const unique: SessionTodo[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    unique.push(cloneTodo(row, typeof row.parentId === "string" ? row.parentId : null));
  }

  const rootIds = new Set(unique.filter((row) => !row.parentId).map((row) => row.id));
  const roots: SessionTodo[] = [];
  const children = new Map<string, SessionTodo[]>();
  for (const rootId of rootIds) children.set(rootId, []);

  for (const row of unique) {
    if (row.parentId && rootIds.has(row.parentId)) {
      children.get(row.parentId)?.push(cloneTodo(row, row.parentId));
    } else {
      roots.push(cloneTodo(row, null));
      if (!children.has(row.id)) children.set(row.id, []);
    }
  }

  return flatten({ roots, children });
}

/**
 * Rows drawn one level in: their parent is a root of the same list. Lists straight from
 * the reducer or the server are tree-consistent, so `Boolean(parentId)` is exact there;
 * use this after filtering rows out, when a child may have lost its parent.
 */
export function isSubtaskIn<T extends { id: string | null; parentId?: string | null }>(
  rows: readonly T[],
): (row: T) => boolean {
  const rootIds = new Set(rows.filter((row) => !row.parentId).map((row) => row.id));
  return (row) => typeof row.parentId === "string" && rootIds.has(row.parentId);
}

/** The anchor that re-inserts `id` where it sits now: its parent and preceding sibling. */
export function anchorOf(rows: readonly SessionTodo[], id: string): TodoAnchor | null {
  const row = rows.find((candidate) => candidate.id === id);
  if (!row) return null;
  const parent = row.parentId ?? null;
  const siblings = rows.filter((candidate) => (candidate.parentId ?? null) === parent);
  const index = siblings.findIndex((candidate) => candidate.id === id);
  return { parent, after: index > 0 ? siblings[index - 1].id : null };
}

/** The anchor that appends a new row at the end of `parent`'s sibling group. */
export function appendAnchor(rows: readonly SessionTodo[], parent: string | null): TodoAnchor {
  const siblings = rows.filter((row) => (row.parentId ?? null) === parent);
  return { parent, after: siblings.at(-1)?.id ?? null };
}

/** Return the target, followed by its children when the target is a root. */
export function expandBlock(rows: readonly SessionTodo[], id: string): SessionTodo[] {
  const normalized = toTreeOrder(rows);
  const target = normalized.find((row) => row.id === id);
  if (!target) return [];
  if (target.parentId) return [target];
  return normalized.filter((row) => row.id === id || row.parentId === id);
}

/** Derive dense server ranks from projection order. */
export function renumber(rows: readonly SessionTodo[]): RankedTodo[] {
  const normalized = toTreeOrder(rows);
  const nextRank = new Map<string | null, number>();
  return normalized.map((row) => {
    const parentId = row.parentId ?? null;
    const rank = nextRank.get(parentId) ?? 0;
    nextRank.set(parentId, rank + 1);
    return { ...row, parentId, rank };
  });
}

function applyCompletion(
  rows: readonly SessionTodo[],
  command: Extract<TodoCommand, { kind: "complete" | "uncomplete" }>,
): SessionTodo[] {
  const normalized = toTreeOrder(rows);
  const target = normalized.find((row) => row.id === command.id);
  if (!target) return normalized;
  const completed = command.kind === "complete";
  if (target.completed === completed) return normalized;

  const cascadeIds = new Set(
    command.cascadeIds ??
      (completed && !target.parentId
        ? normalized.filter((row) => row.parentId === target.id).map((row) => row.id)
        : []),
  );
  const updated = normalized.map((row) => {
    if (row.id === target.id || (completed && cascadeIds.has(row.id))) {
      return { ...row, completed };
    }
    return row;
  });
  const parts = treeParts(updated);
  const parentId = target.parentId ?? null;
  const siblings = parentId === null ? parts.roots : parts.children.get(parentId) ?? [];
  const index = siblings.findIndex((row) => row.id === target.id);
  if (index < 0) return flatten(parts);
  const [moved] = siblings.splice(index, 1);
  siblings.splice(completed ? siblings.length : 0, 0, moved);
  if (parentId === null) parts.roots = siblings;
  else parts.children.set(parentId, siblings);
  return flatten(parts);
}

function applyMove(
  rows: readonly SessionTodo[],
  command: Extract<TodoCommand, { kind: "move" }>,
): SessionTodo[] {
  const normalized = toTreeOrder(rows);
  const target = normalized.find((row) => row.id === command.id);
  if (!target) return normalized;
  const currentParent = target.parentId ?? null;
  if (command.at.parent !== currentParent || command.at.after === target.id) return normalized;

  const parts = treeParts(normalized);
  const siblings = currentParent === null ? parts.roots : parts.children.get(currentParent) ?? [];
  const currentIndex = siblings.findIndex((row) => row.id === target.id);
  if (currentIndex < 0) return normalized;
  const [moved] = siblings.splice(currentIndex, 1);
  siblings.splice(insertionIndex(siblings, command.at.after), 0, moved);
  if (currentParent === null) parts.roots = siblings;
  else parts.children.set(currentParent, siblings);
  return flatten(parts);
}

function applyIndent(rows: readonly SessionTodo[], id: string): SessionTodo[] {
  const parts = treeParts(rows);
  const index = parts.roots.findIndex((row) => row.id === id);
  if (index <= 0 || (parts.children.get(id)?.length ?? 0) > 0) return flatten(parts);

  const [row] = parts.roots.splice(index, 1);
  parts.children.delete(id);
  const parent = parts.roots[index - 1];
  const siblings = parts.children.get(parent.id) ?? [];
  siblings.push(cloneTodo(row, parent.id));
  parts.children.set(parent.id, siblings);
  return flatten(parts);
}

function applyOutdent(rows: readonly SessionTodo[], id: string): SessionTodo[] {
  const parts = treeParts(rows);
  const row = flatten(parts).find((candidate) => candidate.id === id);
  if (!row?.parentId) return flatten(parts);

  const siblings = parts.children.get(row.parentId) ?? [];
  const childIndex = siblings.findIndex((candidate) => candidate.id === id);
  if (childIndex < 0) return flatten(parts);
  const [promoted] = siblings.splice(childIndex, 1);
  parts.children.set(row.parentId, siblings);
  const parentIndex = parts.roots.findIndex((candidate) => candidate.id === row.parentId);
  parts.roots.splice(parentIndex + 1, 0, cloneTodo(promoted, null));
  parts.children.set(promoted.id, []);
  return flatten(parts);
}

/** Apply one projection-level command. The returned array is always in tree order. */
export function applyCommand(rows: readonly SessionTodo[], command: TodoCommand): SessionTodo[] {
  const normalized = toTreeOrder(rows);
  switch (command.kind) {
    case "create": {
      if (normalized.some((row) => row.id === command.id)) return normalized;
      return insertAtAnchor(
        normalized,
        {
          id: command.id,
          text: command.text,
          completed: command.completed,
        },
        command.at,
      );
    }
    case "setText":
      return normalized.map((row) =>
        row.id === command.id ? { ...row, text: command.text } : row,
      );
    case "complete":
    case "uncomplete":
      return applyCompletion(normalized, command);
    case "move":
      return applyMove(normalized, command);
    case "indent":
      return applyIndent(normalized, command.id);
    case "outdent":
      return applyOutdent(normalized, command.id);
    case "delete": {
      const deletedIds = new Set(
        command.deleteIds ?? expandBlock(normalized, command.id).map((row) => row.id),
      );
      return toTreeOrder(normalized.filter((row) => !deletedIds.has(row.id)));
    }
    case "undelete": {
      let restored = normalized;
      for (const entry of command.rows ?? []) {
        if (restored.some((row) => row.id === entry.row.id)) continue;
        restored = insertAtAnchor(restored, entry.row, entry.at);
      }
      return toTreeOrder(restored);
    }
  }
}
